/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Popover, PopoverTitle, PopoverContent, OverlayTrigger } from 'react-bootstrap'
import moment from 'moment';
import 'moment-lunar';
import resource from './resources/resource.json';

import { IEcommCalendarViewProps } from './ecomm-calendar';

import { getCalendarEventsAsync } from '../../actions/DataActionExtension.g';

import { isMobile } from './../../components/breakpoints';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';

/**
 *
 * EcommUpcomingProgrammes component
 * @extends {React.PureComponent<IEcommUpcomingProgrammesProps<IEcommCalendarData>>}
 */
class EcommCalendarView extends React.PureComponent<IEcommCalendarViewProps> {
  public state: any = { width: 0, hasMounted: false, events: [], filteredEvents: [], filterOption: 'All' };
  public eventGuid = 0;

  private createEventId() {
    return String(this.eventGuid++)
  }

  public componentDidMount(): void {
    if (!this.state.hasMounted) {
      window.addEventListener('resize', this._updateDimensions);
      // this.getEvents(new Date().getMonth()+1, new Date().getFullYear());

      this.setState({ width: window.innerWidth, hasMounted: true });
    }
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async getEvents(startDay: number, startMonth: number, startYear: number, endDay: number, endMonth: number, endYear: number) {
    let startDate = new Date(startYear, startMonth, startDay, 0, 0, 0, 0);
    let endDate = new Date(endYear, endMonth, endDay, 23, 59, 0, 0);

    let language = this.props.context.request.locale.split('-')[0];
    let organisation = this.props.config.organisation ? this.props.config.organisation! : 'BT';
    const data = getCalendarEventsAsync({ callerContext: this.props.context.actionContext }, this.props.config.isUat, language, organisation, startDate.toISOString(), endDate.toISOString());

    while (data.status !== 'SUCCESS') {
      if (data.status === 'FAILED')
        break;
      await this.delay(100);
    }

    if (data.result && data.result!.length > 0) {
      let calendarEvents: any = [];
      let calendarJson: any = [];

      data.result!.map((event, index) => {
        let eventJson = JSON.parse(event);

        calendarEvents.push(this.convertToEvent(eventJson, organisation));
        calendarJson.push(eventJson);
      });

      this.setState({ events: calendarJson });
      this.filterEvents(this.state['filterOption']);
    }
  }

  public filterEvents(filter: string) {
    let allEvents: [] = this.state['events'];
    let organisation = this.props.config.organisation ? this.props.config.organisation! : 'BT';
    let calendarEvents: any = [];

    if (filter === 'All') {
      allEvents.map((event, index) => {
        calendarEvents.push(this.convertToEvent(event, organisation));
      });
    } else {
      if (filter === 'Ceremony') {
        allEvents.filter(event => event['FinancialType'] === '01').map((event, index) => {
          calendarEvents.push(this.convertToEvent(event, organisation));
        });
      } else if (filter === 'Group Practice') {
        allEvents.filter(event => event['FinancialType'] === '02').map((event, index) => {
          calendarEvents.push(this.convertToEvent(event, organisation));
        });
      } else {
        allEvents.filter(event => event['FinancialType'] === '12').map((event, index) => {
          calendarEvents.push(this.convertToEvent(event, organisation));
        });
      }
    }

    this.setState({ filteredEvents: calendarEvents, filterOption: filter });
  }

  private convertToEvent(eventJson: any, organisation: string): any {
    let eventColor = '';
    let url = this.props.config.eventPdpUrl.destinationUrl + '?id=' + eventJson['EcommerceId'];

    if (eventJson['FinancialType'] === '01') {
      if (organisation === 'BT') {
        eventColor = '#C83232';
      } else {
        eventColor = '#a27e35';
      }
    }
    else if (eventJson['FinancialType'] === '02') {
      if (organisation === 'BT') {
        eventColor = '#008C64';
      } else {
        eventColor = '#d2ae43';
      }
    }
    else {
      if (organisation === 'BT') {
        eventColor = '#9e7c10';
      } else {
        eventColor = '#d2ae43';
      }
      url = "/ecomm/" + eventJson['EcommerceId'] + ".p";
    }

    return ({ id: this.createEventId(), title: eventJson['Name'], start: eventJson['StartDateTime'].replace('Z', ''), end: eventJson['EndDateTime'].replace('Z', ''), url: url, backgroundColor: eventColor, textColor: 'white' });
  }

  public render(): JSX.Element | null {
    if (!this.state.hasMounted) {
      return null;
    }

    const mobileMode = isMobile(this.state.width);
    let language = this.props.context.request.locale.split('-')[0];

    if (!mobileMode) {
      return (
        <div className='row'>
          <div id='calendar' className='col-12' style={{ color: '#000' }}>
            {this.props.config.organisation === 'BT' &&
              <FullCalendar
                plugins={[dayGridPlugin]}
                locale={language}
                customButtons={{
                  allButton: {
                    text: resource.allText[`${language}`],
                    click: () => {
                      this.filterEvents('All');
                    }
                  },
                  groupPracticeButton: {
                    text: resource.groupPracticeText[`${language}`],
                    click: () => {
                      this.filterEvents('Group Practice');
                    }
                  },
                  ceremonyButton: {
                    text: resource.ceremonyText[`${language}`],
                    click: () => {
                      this.filterEvents('Ceremony');
                    }
                  },
                  courseButton: {
                    text: resource.eventText[`${language}`],
                    click: () => {
                      this.filterEvents('Event');
                    }
                  }
                }}
                headerToolbar={{
                  left: 'prev title next today',
                  center: '',
                  right: 'allButton groupPracticeButton ceremonyButton courseButton'
                }}
                buttonText={{
                  today: resource.todayText[`${language}`],
                }}
                initialView="dayGridMonth"
                firstDay={1}
                editable={false}
                eventDisplay='block'
                nextDayThreshold={'00:00:00'}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={false}
                fixedWeekCount={false}
                showNonCurrentDates={false}
                weekends={true}
                events={this.state['filteredEvents']}
                datesSet={(dateInfo) => {
                  let startDate = dateInfo.start;
                  startDate = new Date(startDate.setMonth(startDate.getMonth() - 1));
                  let endDate = dateInfo.end;
                  endDate = new Date(endDate.setMonth(endDate.getMonth() + 1));

                  this.getEvents(startDate.getDate(), startDate.getMonth(), startDate.getFullYear(), endDate.getDate(), endDate.getMonth(), endDate.getFullYear());
                }}
                eventContent={(eventInfo) => {
                  return (
                    <OverlayTrigger placement='top' overlay=
                      {<Popover id='popover-basic'>
                        <PopoverTitle>{eventInfo.event.title}</PopoverTitle>
                        <PopoverContent>
                          <div className='row'>
                            <div className='col-6'>
                              Start Date:<br />
                              {moment(eventInfo.event.start).format('DD-MM-YYYY hh:mm A')}
                            </div>
                            <div className='col-6'>
                              End Date:<br />
                              {moment(eventInfo.event.end).format('DD-MM-YYYY hh:mm A')}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>}>
                      <p>{eventInfo.event.title}</p>
                    </OverlayTrigger>
                  );
                }}
                dayHeaderContent={(headerInfo) => {
                  return <p>{this._dayHeader(headerInfo.date)}</p>
                }}
                dayCellContent={(dayInfo) => {
                  return (
                    <div style={{ width: '100%', padding: '.5rem' }}>
                      <p style={{ display: 'inline' }}>{this._convertToLunar(dayInfo.date)}</p>
                      <p style={{ float: 'right' }}>{dayInfo.dayNumberText}</p>
                    </div>
                  );
                }}
              />}
            {this.props.config.organisation === 'GP' &&
              <FullCalendar
                plugins={[dayGridPlugin]}
                locale={language}
                customButtons={{
                  allButton: {
                    text: resource.allText[`${language}`],
                    click: () => {
                      this.filterEvents('All');
                    }
                  },
                  groupPracticeButton: {
                    text: resource.groupPracticeText[`${language}`],
                    click: () => {
                      this.filterEvents('Group Practice');
                    }
                  },
                  ceremonyButton: {
                    text: resource.ceremonyText[`${language}`],
                    click: () => {
                      this.filterEvents('Ceremony');
                    }
                  }
                }}
                headerToolbar={{
                  left: 'prev title next today',
                  center: '',
                  right: 'allButton groupPracticeButton ceremonyButton'
                }}
                buttonText={{
                  today: resource.todayText[`${language}`],
                }}
                initialView="dayGridMonth"
                firstDay={1}
                editable={false}
                eventDisplay='block'
                nextDayThreshold={'00:00:00'}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={false}
                fixedWeekCount={false}
                showNonCurrentDates={false}
                weekends={true}
                events={this.state['filteredEvents']}
                datesSet={(dateInfo) => {
                  let startDate = dateInfo.start;
                  startDate = new Date(startDate.setMonth(startDate.getMonth() - 1));
                  let endDate = dateInfo.end;
                  endDate = new Date(endDate.setMonth(endDate.getMonth() + 1));

                  this.getEvents(startDate.getDate(), startDate.getMonth(), startDate.getFullYear(), endDate.getDate(), endDate.getMonth(), endDate.getFullYear());
                }}
                eventContent={(eventInfo) => {
                  return (
                    <OverlayTrigger placement='top' overlay=
                      {<Popover id='popover-basic'>
                        <PopoverTitle>{eventInfo.event.title}</PopoverTitle>
                        <PopoverContent>
                          <div className='row'>
                            <div className='col-6'>
                              Start Date:<br />
                              {moment(eventInfo.event.start).format('DD-MM-YYYY hh:mm A')}
                            </div>
                            <div className='col-6'>
                              End Date:<br />
                              {moment(eventInfo.event.end).format('DD-MM-YYYY hh:mm A')}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>}>
                      <p>{eventInfo.event.title}</p>
                    </OverlayTrigger>
                  );
                }}
                dayHeaderContent={(headerInfo) => {
                  return <p>{this._dayHeader(headerInfo.date)}</p>
                }}
                dayCellContent={(dayInfo) => {
                  return (
                    <div style={{ width: '100%', padding: '.5rem' }}>
                      <p style={{ display: 'inline' }}>{this._convertToLunar(dayInfo.date)}</p>
                      <p style={{ float: 'right' }}>{dayInfo.dayNumberText}</p>
                    </div>
                  );
                }}
              />}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this._updateDimensions);
  }

  private _updateDimensions = () => {
    this.setState({ width: window.innerWidth });
  }

  private _dayHeader(date: Date): string {
    let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let daysChinese = ['日', '一', '二', '三', '四', '五', '六'];

    return daysChinese[date.getDay()] + ' ' + days[date.getDay()];
  }

  private _convertToLunar(date: Date): string {
    let monthChinese = ['正', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];
    let dayChinese = ['初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十', '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十', '卅一'];
    let lunarDate = moment().year(date.getFullYear()).month(date.getMonth()).date(date.getDate()).lunar();

    return monthChinese[lunarDate.month()] + '月' + dayChinese[lunarDate.date() - 1];
  }
}

export default EcommCalendarView;
